// The hydration handshake, as one collaborator: N items are outstanding — tell me when
// they've all filled.
//
// A client-rendered collection RENDERS an item (synchronously, placing it in its final DOM
// home) but cannot itself await the item's fill: the item controller doesn't exist yet
// (Stimulus builds it async, after placement), and the fill is the ITEM's work. So each
// render mints a DEFERRED here; the item resolves it when its own fill completes, and the
// collection awaits them all. That's what makes "hydrated" mean FILLED, not merely placed.
//
//   const filled = pending.register(id, entry)  // collection, at render time
//   const { entry, resolveFilled } = pending.take(id)  // item, on connect (consume-once)
//   await pending.allFilled()                   // collection, end of hydrate
//
// take() is CONSUME-ONCE (get + delete) on purpose: it makes the map's contents mean
// exactly "rendered but not yet connected", which is the half of the item census that
// connected-controller enumeration structurally cannot see (see Collection#slugs).
export class PendingItems {
  constructor() {
    this._entries = new Map() // id -> { entry, resolveFilled }
    this._fills = [] // every deferred minted this hydration cycle
  }

  // Register a rendered-but-unconnected item's real entry and mint its fill deferred.
  // Returns the promise the collection waits on (also accumulated for allFilled).
  register(id, entry) {
    let resolveFilled
    const filled = new Promise((r) => { resolveFilled = r })
    this._entries.set(id, { entry, resolveFilled })
    this._fills.push(filled)
    return filled
  }

  // The item claims its entry on connect — consume-once (get + delete). undefined means a
  // connect with no matching register: a real bug, surfaced rather than papered over.
  take(id) {
    const obj = this._entries.get(id)
    this._entries.delete(id)
    return obj
  }

  // Everything registered and not yet claimed — i.e. rendered, not yet connected.
  entries() {
    return [...this._entries.values()].map((o) => o.entry)
  }

  // Drop a registration whose element was REMOVED before it ever connected. Without this
  // the entry would stay pending forever and the census would keep reporting a row that
  // isn't there. Resolves the fill deferred on the way out, so a hydrate awaiting it can't
  // hang on an item that will now never connect.
  forget(id) {
    const obj = this._entries.get(id)
    if (!obj) return false
    this._entries.delete(id)
    obj.resolveFilled()
    return true
  }

  // Await every fill minted since the last reset. A fill that THROWS still resolves its
  // deferred (the item resolves in a .finally), so this can't hang on a broken fill —
  // readiness must always settle.
  allFilled() {
    return Promise.all(this._fills)
  }

  // Start a fresh hydration cycle: forget the previous cycle's fill deferreds so
  // allFilled() waits on THIS cycle only. Outstanding unclaimed entries are kept — they
  // are still legitimately "rendered, not yet connected".
  beginCycle() {
    this._fills = []
  }
}
